import { yupResolver } from '@hookform/resolvers/yup';
import { ChevronLeftIcon, EnterIcon, MinusIcon } from '@radix-ui/react-icons';
import { Box, Button, Card, Flex, Spinner } from '@radix-ui/themes';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation } from 'react-query';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import RadioGroupComponent from '../../components/inputs/RadioGroup/RadioGroupComponent';
import SelectComponent from '../../components/inputs/Select/SelectComponent';
import TextFieldComponent from '../../components/inputs/Textfield/TextfieldComponent';
import { useAppDispatch } from '../../hooks/hooks';
import { useGetCoursePlansQuery } from '../../hooks/useGetCoursePlansQuery';
import { useGetCoursesQuery } from '../../hooks/useGetCoursesQuery';
import { useGetDepartamentsQuery } from '../../hooks/useGetDepartamentsQuery';
import { useGetFacultiesQuery } from '../../hooks/useGetFacultiesQuery';
import { useGetScientificAreasQuery } from '../../hooks/useGetScientificAreasQuery';
import { resetPreviousPath } from '../../redux/slices/previousPath';
import { resetUpdateCourseData } from '../../redux/slices/school/course';
import { RootState } from '../../redux/store';
import { routes } from '../../routes';
import { createPlanSubject, getPlanSubjects, updatePlanSubject } from '../../services/school/PlanSubjects';
import { getScientificAreas } from '../../services/school/scientificArea/getScientificAreas';
import { getSubjects } from '../../services/school/subject/getSubjects';
import { CreatePlanSubjectType, PlanSubjectType } from '../../types/PlanSubject';
import { SubjectType } from '../../types/Subject';
import { CreatePlanSubjectFormValidation } from './CreatePlanSubjectsFormValidation';
import { useGetUniversitiesQuery } from '../../hooks/useGetUniversitiesQuery';
import { resetLoaderAvailable, setLoaderAvailable } from '../../redux/slices/loaderController';

export default function CreateUpdatePlanSubjects() {
    const [currentPage, setCurrentPage] = useState(1);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const mutation = useMutation({ mutationFn: createPlanSubject, mutationKey: 'createPlanSubject' });
    const mutationUpdate = useMutation({ mutationFn: updatePlanSubject, mutationKey: 'updatePlanSubject' });
    const updateCourseFormData = useSelector((state: RootState) => state.course);
    const previousPath = useSelector((state: RootState) => state.previousPath);

    type CreatePlanSubjectsTypeFormType = {
        idUniversity?: string;
        idFaculty: string;
        idDepartament: string;
        idCourse: string;
        idPlan: string;
        planSubjects: {
            id?: string | null | undefined;
            idPlan?: string | null | undefined;
            idScientificArea: string;
            idSubject: string;
            isCore: boolean;
            hasExam: boolean;
            semester: number;
            level: number;
            credits: number;
            subjects?: SubjectType[];
        }[];
    };
    const defaultValues: CreatePlanSubjectsTypeFormType = {
        idFaculty: updateCourseFormData && updateCourseFormData?.id ? updateCourseFormData.idFaculty : '',
        idDepartament: updateCourseFormData && updateCourseFormData?.id ? updateCourseFormData.idDepartament : '',
        idCourse: updateCourseFormData && updateCourseFormData?.id ? updateCourseFormData.id : '',
        idPlan: updateCourseFormData && updateCourseFormData?.id ? updateCourseFormData.idPlan : '',
        planSubjects: updateCourseFormData && updateCourseFormData?.id && updateCourseFormData?.planSubjects ? updateCourseFormData?.planSubjects as any : [],
    }
    const { control, handleSubmit, setValue, getValues, formState: { errors } } = useForm<CreatePlanSubjectsTypeFormType>({
        resolver: yupResolver(CreatePlanSubjectFormValidation),
        defaultValues,
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        shouldUseNativeValidation: false,
    });
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'planSubjects',
    });

    const { data: universities, isLoading: isLoadingUniversities, isError: errorUniversities, refetch: refetchUniversities } = useGetUniversitiesQuery({ PageNumber: 1, PageSize: 400 });
    const { data: faculties, isLoading: isLoadingFaculties, isError: errorFaculties, refetch: refetchFaculties } = useGetFacultiesQuery({ PageNumber: 1, PageSize: 400, IdUniversity: getValues('idUniversity') });
    const { data: departaments, isLoading: isLoadingDepartaments, isError: errorDepartaments, refetch: refreshDepartaments } = useGetDepartamentsQuery(
        { PageNumber: 1, PageSize: 400, IdFaculty: getValues('idFaculty') },
        { enabled: !!getValues('idFaculty') }
    );

    const { data: courses, isLoading: isLoadingCourses, isError: errorCourses, refetch: refreshCourses } = useGetCoursesQuery(
        { PageNumber: currentPage, PageSize: 400, IdDepartament: getValues('idDepartament') },
        { enabled: !!getValues('idDepartament') });

    const { data: coursePlans, isLoading: isLoadingCoursePlans, isError: errorCoursePlans, refetch: refreshCoursePlans } = useGetCoursePlansQuery(
        { PageNumber: currentPage, PageSize: 400, IdCourse: getValues('idCourse') },
        { enabled: !!getValues('idCourse') });

    const { data: scientificAreas, isLoading: isLoadingScientificAreas, isError: errorScientificAreas } = useGetScientificAreasQuery(
        { PageNumber: currentPage, PageSize: 400 });

    const onSubmit = handleSubmit(async (data) => {
        try {
            const planSubjectsToSave = data.planSubjects.filter(ps => !ps.id).map(ps => {
                const planSubject = {
                    ...ps,
                    credits: ps.credits,
                    idPlan: data.idPlan,
                    status: true,
                    subjects: [ps.idSubject]
                } as unknown as CreatePlanSubjectType
                return mutation.mutateAsync(planSubject)
            })
            const planSubjectsToEdit = data.planSubjects.filter(ps => ps.id).map(ps => {
                const planSubject = {
                    ...ps,
                    credits: ps.credits,
                    idPlan: data.idPlan,
                    status: true,
                } as unknown as PlanSubjectType
                return mutationUpdate.mutateAsync(planSubject)
            })
            await Promise.all(planSubjectsToSave)
            if (planSubjectsToEdit && planSubjectsToEdit.length > 0) {
                await Promise.all(planSubjectsToEdit)
            }
            toast.success("Disciplina adicionada ao plano de curso");
            dispatch(resetUpdateCourseData());
            dispatch(resetPreviousPath());
            handleBack();
        } catch (err: any) {
            toast.error(`${mutation.error ?? err?.message}`);
        }
    });

    const handleBack = () => {
        const path = previousPath.previousPath!;
        dispatch(resetPreviousPath());
        dispatch(resetUpdateCourseData());
        if (path) {
            navigate(path);
            return;
        }
        navigate(routes.planSubjects);
    }

    const handleFacultyChange = (idFaculty: string) => {
        setValue('idFaculty', idFaculty);
        refreshDepartaments();
    };

    const handleDepartamentChange = (idDepartament: string) => {
        setValue('idDepartament', idDepartament);
        refreshCourses();
    };
    const handleCourseChange = async (idCourse: string) => {
        setValue('idCourse', idCourse);
        refreshCoursePlans();
    };

    const handleCoursePlanChange = async (idPlan: string) => {

        try {
            dispatch(setLoaderAvailable(true));
            setValue('idPlan', idPlan);
            setValue('planSubjects', []);

            const planSubjects = await getPlanSubjects({
                PageSize: 4000,
                PageNumber: 1,
                IdPlan: idPlan
            });
            setValue('planSubjects', planSubjects.items as any);

            const allScientificAreas = await getScientificAreas({ PageSize: 400, PageNumber: 1 });

            if (allScientificAreas?.items) {
                const updatedFilteredSubjects: { [index: number]: SubjectType[] } = {};

                await Promise.all(
                    planSubjects.items.map(async (planSubject: any, index: number) => {
                        if (planSubject.idScientificArea) {
                            const subjects = await getSubjects({
                                PageSize: 400,
                                PageNumber: 1,
                                idScientificArea: planSubject.idScientificArea,
                            });

                            updatedFilteredSubjects[index] = subjects.items ?? [];
                        }
                    })
                );

                setFilteredSubjects(updatedFilteredSubjects);
            }
        } catch (ex) {
            console.error("Erro ao carregar disciplinas:", ex);
        }
        finally{
                        dispatch(resetLoaderAvailable());

        }
    };

    const [filteredSubjects, setFilteredSubjects] = useState<{ [key: number]: SubjectType[] }>({});

    const handleScientificAreaChange = async (idScientificArea: string, index: number) => {
        setValue(`planSubjects.${index}.idScientificArea`, idScientificArea);
        setValue(`planSubjects.${index}.idSubject`, '');
        const subjects = await getSubjects({ PageSize: 400, PageNumber: 1, idScientificArea: idScientificArea });
        if (subjects?.items) {
            const rowFilteredSubjects = subjects.items.filter(
                subject => subject.idScientificArea === idScientificArea
            );
            setFilteredSubjects(prev => ({
                ...prev,
                [index]: rowFilteredSubjects
            }));
        }
    };
    const removePlanSubject = async (planSubject: PlanSubjectType) => {
        try {
            const idPlan = getValues('idPlan');
            planSubject.idPlan = idPlan;
            planSubject.status = false;
            await mutationUpdate.mutateAsync(planSubject)
            toast.success("Removido!")
        } catch (ex) {
            toast.error("Ops, Não foi possivel remover")
            console.error(ex)
        }

    };

    const handleUniversityChange = (id: string) => {
        setValue('idUniversity', id);
        setValue('idFaculty', '');
        setValue('idDepartament', '');
        refetchFaculties();
    };

    useEffect(() => {
        const loadInitialSubjects = async () => {
            if (updateCourseFormData?.id && updateCourseFormData?.planSubjects) {
                const subjectsPromises = updateCourseFormData?.planSubjects?.map(async (planSubject: any, index: number) => {
                    if (planSubject?.idScientificArea) {
                        const subjects = await getSubjects({
                            PageSize: 400,
                            PageNumber: 1,
                            idScientificArea: planSubject?.idScientificArea
                        });

                        if (subjects?.items) {
                            setFilteredSubjects(prev => ({
                                ...prev,
                                [index]: subjects.items
                            }));
                        }
                    }
                });

                await Promise.all(subjectsPromises);
            }
        };
        loadInitialSubjects();
    }, [updateCourseFormData?.planSubjects, updateCourseFormData?.id]);

    return (
        <form onSubmit={onSubmit} noValidate>
            <Card size='1'>
                <Flex direction='column' gap='4'>

                    <Flex direction='column' gap='4'>
                        <SelectComponent
                            name="idUniversity"
                            label="Instituição de Ensino Superior"
                            control={control}
                            errors={errors}
                            style={{ width: '100%' }}
                            options={universities?.items.map(u => ({
                                value: u.id ?? "",
                                label: u.description ?? ""
                            }))}
                            itemsLoading={isLoadingUniversities}
                            itemsError={errorUniversities}
                            onSelect={handleUniversityChange}
                            required={true}
                        />
                        <SelectComponent name="idFaculty" label='Unidade orgânica' control={control} errors={errors} style={{ width: '100%' }}
                            options={faculties?.items.map(faculty => ({
                                value: faculty.id ?? "",
                                label: faculty.description ?? ""
                            }))}
                            onSelect={handleFacultyChange}
                            required={true}
                        />
                    </Flex>
                    <Flex gap='4'>
                        <SelectComponent name="idDepartament" label='Departamento' control={control} errors={errors} style={{ width: '100%' }}
                            options={departaments?.items.map(departament => ({
                                value: departament.id ?? "",
                                label: departament.description ?? ""
                            }))}
                            onSelect={handleDepartamentChange}
                            required={true}
                        />
                        <SelectComponent name="idCourse" label='Curso' control={control} errors={errors} style={{ width: '100%' }}
                            options={courses?.items.map(course => ({
                                value: course.id ?? "",
                                label: course.description ?? ""
                            }))}
                            onSelect={
                                handleCourseChange
                            }
                            required={true}
                        />
                    </Flex>
                    <SelectComponent name='idPlan' label='Plano de curso' control={control} errors={errors} style={{ width: '100%' }}
                        options={coursePlans?.items.map(coursePlan => ({
                            value: coursePlan.id ?? '',
                            label: `${coursePlan.course} - ${coursePlan.academicYear}`
                        }))}
                        onSelect={handleCoursePlanChange}
                        required={true}
                    />
                    {fields.map((field, index) => {
                        return (
                            <Card key={field.id} size="2" style={{ marginBottom: '1rem' }}>
                                <Flex direction="column" gap="2">

                                    <Flex justify='end'>
                                        <Button onClick={async (e) => {
                                            e.preventDefault();
                                            const planSubject = getValues(`planSubjects.${index}`)
                                            await removePlanSubject(planSubject as PlanSubjectType)
                                            remove(index)
                                        }
                                        } color="red"><MinusIcon width="18" height="18" />
                                        </Button>
                                    </Flex>
                                    <Flex direction='row' gap="3">
                                        <SelectComponent
                                            name={`planSubjects.${index}.idScientificArea`}
                                            label="Área Científica"
                                            control={control}
                                            errors={errors}
                                            style={{ width: '100%' }}
                                            options={scientificAreas?.items.map((area) => ({
                                                value: area.id ?? '',
                                                label: area.description ?? '',
                                            }))}
                                            onSelect={async (id: string) => {
                                                await handleScientificAreaChange(id, index);
                                            }}
                                        />
                                        <SelectComponent
                                            name={`planSubjects.${index}.idSubject`}
                                            label="Disciplina"
                                            control={control}
                                            errors={errors}
                                            style={{ width: '100%' }}
                                            options={filteredSubjects[index]?.map((subject) => ({
                                                value: subject.id ?? '',
                                                label: subject.description ?? '',
                                            })) ?? []}
                                        />

                                    </Flex>
                                    <Flex direction='row' gap="3">
                                        <TextFieldComponent
                                            name={`planSubjects.${index}.level`}
                                            label="Ano"
                                            control={control}
                                            errors={errors}
                                            fieldProps={{ placeholder: 'Ano' }}
                                        />
                                        <TextFieldComponent
                                            name={`planSubjects.${index}.semester`}
                                            label="Semestre"
                                            control={control}
                                            errors={errors}
                                            fieldProps={{ placeholder: 'Semestre' }}
                                        />

                                        <TextFieldComponent
                                            name={`planSubjects.${index}.credits`}
                                            label="Créditos"
                                            control={control}
                                            errors={errors}
                                            fieldProps={{ placeholder: 'Créditos' }}
                                        />
                                        <RadioGroupComponent
                                            name={`planSubjects.${index}.isCore`}
                                            label="Tipo de Disciplina"
                                            control={control}
                                            errors={errors}
                                            options={[
                                                { value: 'true', label: 'Nuclear' },
                                                { value: 'false', label: 'Opcional' },
                                            ]}
                                            fieldProps={{
                                                style: { display: 'flex', flexDirection: 'row', gap: '16px' },
                                            }}
                                        />

                                        <RadioGroupComponent
                                            name={`planSubjects.${index}.hasExam`}
                                            label="Tem Exame?"
                                            control={control}
                                            errors={errors}
                                            options={[
                                                { value: 'true', label: 'Sim' },
                                                { value: 'false', label: 'Não' },
                                            ]}
                                            fieldProps={{
                                                style: { display: 'flex', flexDirection: 'row', gap: '16px' },
                                            }}
                                        />
                                    </Flex>

                                </Flex>
                            </Card>
                        );
                    })}
                    <Box>
                        <Button
                            variant="soft"
                            onClick={(e) => {
                                e.preventDefault()
                                append({
                                    idScientificArea: '',
                                    idSubject: '',
                                    isCore: false,
                                    hasExam: false,
                                    semester: 1,
                                    level: 1,
                                    credits: 0,
                                })
                            }
                            }
                        >
                            Adicionar Disciplina
                        </Button>
                    </Box>

                    <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
                        <Button color='red' onClick={handleBack} disabled={mutation.isLoading}>
                            <ChevronLeftIcon /> voltar
                        </Button>
                        <Button type="submit" disabled={mutation.isLoading} variant='surface'>
                            {mutation.isLoading ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
                            Registar
                        </Button>
                    </Flex>
                </Flex>
            </Card>
        </form>
    );
}